import { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { docTitle } from '../domain/documentTitle';
import { api, getToken, mintViewerToken } from '../api';
import DocumentPreview from './preview/DocumentPreview';
import EvidenceCallout from './EvidenceCallout';
import UpgradeTranscriptModal from './UpgradeTranscriptModal';
import SpeakerLabelPopover from './SpeakerLabelPopover';
import TranscriptUpgradePanel from './TranscriptUpgradePanel';
import useTranscriptUpgrade from '../hooks/useTranscriptUpgrade';
import useHelpContextSignal from '../hooks/useHelpContextSignal';
import useRedactions from '../hooks/useRedactions';
import RedactionStudio from './redaction/RedactionStudio';
import VideoRedactionOverlay from './redaction/VideoRedactionOverlay';
import useExcerpt from '../hooks/useExcerpt';
import ExcerptStudio from './excerpt/ExcerptStudio';
import {
  logLocateAttempt, logLocateSuccess, logLocateFail, logHighlight,
  logMediaFetchStarted, logMediaFetchFailed,
} from './preview/viewerDebug';

/**
 * DocumentViewer — full-screen content viewer for documents.
 *
 * Focused purely on consuming the document content:
 *   - PDFs: page-by-page reader with zoom and navigation
 *   - Video/Audio: embedded player with time-synced transcript sidebar
 *   - Images: full-resolution image viewer
 *
 * No metadata, no processing info — just the content.
 *
 * Props:
 *   documentId — UUID of the document to view
 *   onClose    — callback to close the viewer
 */

const MEDIA_EXTENSIONS = {
  video: new Set(['mp4', 'webm', 'mov', 'avi', 'mkv', 'wmv', 'flv', '3gp']),
  audio: new Set(['mp3', 'wav', 'm4a', 'ogg', 'flac']),
};
const IMAGE_EXTENSIONS = new Set(['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'tiff', 'tif', 'heic', 'heif']);

function fmtTime(s) {
  if (!s && s !== 0) return '';
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${sec.toString().padStart(2, '0')}`;
}

// ── HLS loader (shared singleton, lazily bundled) ─────────────
// hls.js is self-hosted via `npm install hls.js@1.6.16` so we don't
// depend on a moving cdn.jsdelivr `@1` tag (the EmptyRanges
// ReferenceError that broke image preview on 2026-05-21 came from a
// silent CDN bump). Vite emits this dynamic import as its own chunk;
// non-video sessions never pay the ~100KB.
let _hlsPromise = null;
function loadHls() {
  if (_hlsPromise) return _hlsPromise;
  _hlsPromise = import('hls.js')
    .then(mod => mod.default || mod)
    .catch(err => { _hlsPromise = null; throw err; });
  return _hlsPromise;
}

import useDocumentSegments, { rulingLabel } from '../hooks/useDocumentSegments';
import SegmentsRail from './preview/SegmentsRail';
import UndoToast, { useUndoToast } from './segments/UndoToast';

export default function DocumentViewer({ documentId, onClose, onOpenDocument = null, startAt = null, initialPage = null, query = null, chunkId = null, citeRange = null, anchor = null, headerActions = null, standalone = false }) {
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [textData, setTextData] = useState(null);
  const [textLoading, setTextLoading] = useState(true);
  const [textReloadKey, setTextReloadKey] = useState(0);
  const reloadTranscript = useCallback(() => setTextReloadKey(k => k + 1), []);

  // Evidence-callout state. The viewer always shows what was cited
  // alongside the doc; the renderer reports back whether it succeeded
  // in drawing a pixel-accurate highlight so we can label the panel
  // ("Highlighted" vs "Source confirmed; not pinpointed").
  const [highlightStatus, setHighlightStatus] = useState('pending'); // 'pending' | 'located' | 'no-location'
  // Surface the renderer-resolved snippet too — the SOTA path knows
  // the verbatim chunk text, which is more authoritative than what
  // the URL `q=` carries.
  const [resolvedSnippet, setResolvedSnippet] = useState(null);

  // Phase 1: Load core metadata (already includes page_count)
  // Phase 2: Load text/transcript in background (can be huge for media)
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setTextLoading(true);
    setError(null);

    // Fast path — single call, doc metadata already includes page_count
    api.get(`/documents/${documentId}`)
      .then(docData => {
        if (cancelled) return;
        setDoc(docData);
        setLoading(false);
      })
      .catch(err => { if (!cancelled) { setError(err.message); setLoading(false); } });

    // Slow path — text/transcript loads in parallel but doesn't block render
    api.get(`/documents/${documentId}/view/text`)
      .catch(() => ({}))
      .then(text => { if (!cancelled) { setTextData(text); setTextLoading(false); } });

    return () => { cancelled = true; };
  }, [documentId, textReloadKey]);

  // Auto-refresh while an upgrade-tier transcription is running. The
  // background job updates extraction.upgrade_job; polling /view/text
  // lets the chat-log appear without a manual reload after the user
  // clicks Generate Full Transcript and walks away.
  const upgradeJobStatus = textData?.upgrade_job?.status || null;
  const upgradeInFlight = upgradeJobStatus === 'processing' || upgradeJobStatus === 'queued';
  useEffect(() => {
    if (!upgradeInFlight) return;
    let cancelled = false;
    const tick = async () => {
      if (cancelled) return;
      try {
        const t = await api.get(`/documents/${documentId}/view/text`);
        if (!cancelled) setTextData(t || {});
      } catch { /* swallow — keep polling */ }
    };
    const id = setInterval(tick, 10000);
    return () => { cancelled = true; clearInterval(id); };
  }, [upgradeInFlight, documentId]);

  // Determine content type
  const ft = (doc?.file_type || '').toLowerCase();
  const contentCat = doc?.canonical_type || doc?.characterization?.content_category || '';
  const isVideo = contentCat === 'video' || MEDIA_EXTENSIONS.video.has(ft);
  const isAudio = contentCat === 'audio' || MEDIA_EXTENSIONS.audio.has(ft);
  const isMedia = isVideo || isAudio;
  const isImage = IMAGE_EXTENSIONS.has(ft) || contentCat === 'image';
  // Documents detected inside this file (compilation PDFs): rail + boundary bands + verbs.
  const { data: segData, rule: segRule, undo: segUndo, busy: segBusy } = useDocumentSegments(documentId, { enabled: !!doc && !isMedia && !isImage && ft === 'pdf' });
  const [jumpRequest, setJumpRequest] = useState(null);
  const [activePage, setActivePage] = useState(initialPage || 1);
  const segRail = !!(segData?.available && (segData.segments || []).length > 1);
  const { toast: segToast, show: showSegToast, clear: clearSegToast } = useUndoToast();
  // every correction answers with what it did + Undo (plan §7d.2)
  const segRuleSafe = async (action, pg, extra, seg) => {
    try { await segRule(action, pg, extra); showSegToast(rulingLabel(action, pg, extra || {}, seg)); }
    catch { /* error surfaces in the hook */ }
  };
  const segUndoSafe = async () => { try { await segUndo(); clearSegToast(); } catch { /* surfaces in the hook */ } };
  const totalPages = doc?.page_count || textData?.page_count || 0;

  // Help-context signals: viewer is open + what kind of content. Format is an
  // enum string, not a filename or doc id.
  let viewerFormat = null;
  if (isVideo) viewerFormat = 'video';
  else if (isAudio) viewerFormat = 'audio';
  else if (isImage) viewerFormat = 'image';
  else if (ft === 'pdf') viewerFormat = 'pdf';
  else if (['docx', 'doc', 'xlsx', 'xls', 'pptx', 'ppt'].includes(ft)) viewerFormat = 'office';
  else if (['txt', 'md'].includes(ft)) viewerFormat = 'text';
  useHelpContextSignal('viewer_open', true);
  useHelpContextSignal('viewer_format', viewerFormat);

  // "Open in a new window": this document in its own browser window
  // (/case/:caseId/view/:docId — see pages/DocumentWindow.jsx), carrying the
  // page it is on, so it can sit beside the list or on a second screen while
  // the list stays put. The window is named by document, so asking again
  // focuses the window that is already open. This viewer closes once the
  // window is open; a browser that blocks the window leaves this one as is.
  const popOut = useCallback(() => {
    if (!doc?.case_id) return;
    const params = new URLSearchParams();
    if (isMedia && Number.isFinite(Number(startAt))) params.set('t', String(startAt));
    else if (!isMedia && activePage > 1) params.set('page', String(activePage));
    const qs = params.toString();
    const url = `/case/${doc.case_id}/view/${documentId}${qs ? `?${qs}` : ''}`;
    const w = window.open(url, `dv-${documentId}`, 'popup=yes,width=1200,height=900');
    if (w) onClose?.();
  }, [doc?.case_id, documentId, isMedia, startAt, activePage, onClose]);

  // ESC to close
  useEffect(() => {
    const handleKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onClose]);

  if (loading) {
    return (
      <div className="dv-overlay" onClick={onClose}>
        <div className="dv-panel" onClick={e => e.stopPropagation()}>
          <div className="dv-loading-skeleton">
            <div className="dv-loading-bar" />
            <div className="dv-loading-label">Loading document…</div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dv-overlay" onClick={onClose}>
        <div className="dv-panel" onClick={e => e.stopPropagation()}>
          <div className="dd-error">{error}</div>
          <button className="btn" onClick={onClose}>Close</button>
        </div>
      </div>
    );
  }

  return (
    <div className="dv-overlay" onClick={onClose}>
      <div className="dv-panel" onClick={e => e.stopPropagation()}>
        {/* Minimal header */}
        <div className="dv-header">
          <h2 title={doc.filename}>{docTitle(doc)}</h2>
          {headerActions && (
            <div className="dv-header-actions">{headerActions({ document: doc })}</div>
          )}
          {!standalone && doc?.case_id && (
            <button
              type="button"
              className="btn btn-sm btn-ghost dv-popout"
              data-testid="dv-popout"
              onClick={popOut}
              title="The document in its own window — put it beside the list, or on your other screen"
            >
              ⧉ Open in a new window
            </button>
          )}
          <button className="dd-close" onClick={onClose} aria-label="Close" title="Close (Esc)">✕</button>
        </div>

        {/* Content area. Media keeps its dedicated player + transcript
            UX; everything else (pdf, spreadsheet, docx, image, text)
            goes through the manifest-driven DocumentPreview dispatcher. */}
        <div className={`dv-body${segRail ? ' dv-body--with-rail' : ''}`} data-np-doc-id={documentId}>
          {segRail && (
            <SegmentsRail
              segments={segData.segments}
              warnings={segData.file_warnings || []}
              unsurePages={segData.unsure_pages || []}
              activePage={activePage}
              onJump={(pg) => setJumpRequest({ page: pg, nonce: Date.now() })}
            />
          )}
          <UndoToast toast={segToast} busy={segBusy} onUndo={segUndoSafe} onClose={clearSegToast} />
          <div className="dv-body-main">
          {isMedia ? (
            <MediaViewer
              documentId={documentId}
              caseId={doc?.case_id}
              isVideo={isVideo}
              textData={textData}
              textLoading={textLoading}
              fileType={ft}
              fileSize={doc?.file_size || 0}
              transcodeStatus={doc?.characterization?.transcode_status}
              startAt={startAt}
              query={query}
              chunkId={chunkId}
              onTranscriptChanged={reloadTranscript}
              onHighlightStatus={setHighlightStatus}
              onResolvedSnippet={setResolvedSnippet}
              redactionOf={doc?.derived_kind === 'redaction' ? (doc?.source_document_id || null) : null}
              excerptRecord={doc?.derived_kind === 'excerpt'
                ? (doc?.characterization?.excerpt || { source_document_id: doc?.source_document_id || null })
                : null}
              onOpenDocument={onOpenDocument}
            />
          ) : (
            <DocumentPreview
              doc={doc}
              documentId={documentId}
              initialPage={initialPage}
              query={query}
              chunkId={chunkId}
              citeRange={citeRange}
              anchor={anchor}
              onHighlightStatus={setHighlightStatus}
              onResolvedSnippet={setResolvedSnippet}
              segments={segData?.available ? segData.segments : null}
              onSegmentRuling={segRuleSafe}
              segmentBusy={segBusy}
              jumpRequest={jumpRequest}
              onActivePageChange={setActivePage}
            />
          )}
          </div>
        </div>

        {/* Evidence callout — always-on panel showing the cited
            text. Independent of whether the renderer succeeded in
            drawing a pixel-accurate highlight. */}
        {(query || resolvedSnippet) && (
          <EvidenceCallout
            snippet={resolvedSnippet || query}
            filename={doc.filename}
            page={initialPage}
            timestamp={startAt}
            verified={true /* citations that reach the viewer are verified */}
            locationStatus={highlightStatus === 'located' ? 'located' : 'no-location'}
            tier={doc.tier}
            documentId={documentId}
            caseId={doc.case_id}
          />
        )}
      </div>
    </div>
  );
}

// ── Image Viewer ─────────────────────────────────────────────
function ImageViewer({ documentId }) {
  const [imgSrc, setImgSrc] = useState(null);
  const [urlSource, setUrlSource] = useState(null);
  const [imgError, setImgError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    let objectUrl = null;

    api.get(`/documents/${documentId}/media-url`)
      .then(d => {
        if (cancelled) return;
        if (d.source === 'gcs_signed') {
          // Direct GCS signed URL — browser fetches from GCS, zero bytes through server
          setImgSrc(d.url);
          setUrlSource('gcs_signed');
          logMediaFetchStarted({ documentId, url_source: 'gcs_signed' });
        } else {
          // Local dev fallback — proxy through server with auth headers
          logMediaFetchStarted({ documentId, url_source: 'api_fallback' });
          return api.raw('GET', `/documents/${documentId}/view`)
            .then(res => {
              if (!res.ok) throw new Error(`HTTP ${res.status}`);
              return res.blob();
            })
            .then(blob => {
              if (!cancelled) {
                objectUrl = URL.createObjectURL(blob);
                setImgSrc(objectUrl);
                setUrlSource('api_fallback');
              }
            });
        }
      })
      .catch(err => {
        if (cancelled) return;
        setImgError(err.message);
        logMediaFetchFailed({
          documentId,
          url_source: 'media_url_failed',
          error_class: err?.name || 'Error',
          error_message: err?.message,
        });
      });

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [documentId]);

  if (imgError) return <p className="text-muted">Failed to load image: {imgError}</p>;
  if (!imgSrc) return <div className="dd-loading">Loading image…</div>;
  return (
    <div className="dv-image-viewer">
      <img
        src={imgSrc}
        alt="Document"
        style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
        onError={async () => {
          setImgError('Image failed to load from storage');
          // Capture HTTP status for the failed fetch — best-effort HEAD.
          let status = null;
          let errorClass = 'img_load_error';
          try {
            const credentials = urlSource === 'gcs_signed' ? 'omit' : 'include';
            const r = await fetch(imgSrc, { method: 'HEAD', credentials });
            status = r.status;
          } catch (fetchErr) {
            errorClass = fetchErr?.name || 'fetch_error';
          }
          logMediaFetchFailed({
            documentId,
            url_source: urlSource,
            status,
            error_class: errorClass,
          });
        }}
      />
    </div>
  );
}

// ── Page Viewer (PDFs) ───────────────────────────────────────
const _pageCache = new Map();
const MAX_PAGE_CACHE = 20;

function _fetchPageBlob(documentId, page) {
  const key = `${documentId}:${page}`;
  if (_pageCache.has(key)) return _pageCache.get(key);
  const promise = api.raw('GET', `/documents/${documentId}/page/${page}?width=1200&quality=90`)
    .then(res => {
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return res.blob();
    })
    .then(blob => URL.createObjectURL(blob))
    .catch(err => { _pageCache.delete(key); throw err; });
  if (_pageCache.size >= MAX_PAGE_CACHE) {
    const oldest = _pageCache.keys().next().value;
    const oldUrl = _pageCache.get(oldest);
    if (oldUrl instanceof Promise) oldUrl.then(u => URL.revokeObjectURL(u)).catch(() => {});
    else if (typeof oldUrl === 'string') URL.revokeObjectURL(oldUrl);
    _pageCache.delete(oldest);
  }
  _pageCache.set(key, promise);
  return promise;
}

function PageViewer({ documentId, totalPages }) {
  const [currentPage, setCurrentPage] = useState(1);
  const [imgSrc, setImgSrc] = useState(null);
  const [imgError, setImgError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setImgSrc(null);
    setImgError(null);

    _fetchPageBlob(documentId, currentPage)
      .then(url => { if (!cancelled) setImgSrc(url); })
      .catch(err => { if (!cancelled) setImgError(err.message); });

    // Prefetch adjacent pages
    if (currentPage < totalPages) _fetchPageBlob(documentId, currentPage + 1).catch(() => {});
    if (currentPage > 1) _fetchPageBlob(documentId, currentPage - 1).catch(() => {});

    return () => { cancelled = true; };
  }, [documentId, currentPage, totalPages]);

  // Keyboard navigation
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'ArrowLeft' && currentPage > 1) setCurrentPage(p => p - 1);
      if (e.key === 'ArrowRight' && currentPage < totalPages) setCurrentPage(p => p + 1);
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [currentPage, totalPages]);

  return (
    <div className="dv-page-viewer">
      <div className="dv-page-nav">
        <button className="btn" disabled={currentPage <= 1} onClick={() => setCurrentPage(p => p - 1)}>← Prev</button>
        <span>Page {currentPage} of {totalPages}</span>
        <button className="btn" disabled={currentPage >= totalPages} onClick={() => setCurrentPage(p => p + 1)}>Next →</button>
      </div>
      <div className="dv-page-content">
        {imgError && <p className="text-muted">Failed to load page: {imgError}</p>}
        {!imgError && !imgSrc && <div className="dd-loading">Rendering page…</div>}
        {imgSrc && <img src={imgSrc} alt={`Page ${currentPage}`} />}
      </div>
    </div>
  );
}

// ── Media Viewer (Video/Audio + Transcript) ──────────────────
function MediaViewer({ documentId, caseId, isVideo, textData, textLoading, fileType, fileSize, transcodeStatus, startAt, query, chunkId, onTranscriptChanged, onHighlightStatus, onResolvedSnippet, redactionOf = null, excerptRecord = null, onOpenDocument = null }) {
  // Google full-coverage upgrade flow — same shared hook the
  // inventory dropdown uses, so the state machine, window.confirm,
  // and bg-job poll behavior stay identical across surfaces.
  const upgrade = useTranscriptUpgrade({
    docId: documentId,
    caseId,
    onTranscriptInvalidated: onTranscriptChanged,
    onDocChange: undefined,
  });

  // upgradeJobStatus is recomputed here from textData (this is a sibling
  // function to DocumentViewer, NOT a closure over its scope — passing
  // bare references would throw "Can't find variable: upgradeJobStatus"
  // at render time).
  const upgradeJobStatus = textData?.upgrade_job?.status
    || textData?.extraction?.upgrade_job?.status
    || null;
  const mediaRef = useRef(null);
  const hlsRef = useRef(null);
  const [mediaUrl, setMediaUrl] = useState(null);
  const [source, setSource] = useState(() => {
    // Instant status from doc metadata — skip API call for known blocked states
    if (transcodeStatus === 'pending' || transcodeStatus === 'processing') return 'transcode_pending';
    if (transcodeStatus === 'failed') return 'transcode_failed';
    return null;
  });
  const [mimeType, setMimeType] = useState(null);
  const [err, setErr] = useState(null);
  const [playErr, setPlayErr] = useState(false);
  const [hlsLoading, setHlsLoading] = useState(false);
  const [statusMessage, setStatusMessage] = useState(() => {
    if (transcodeStatus === 'pending' || transcodeStatus === 'processing')
      return 'Preparing playback… this will start automatically when ready.';
    if (transcodeStatus === 'failed')
      return 'Video processing failed. Our team has been notified.';
    return null;
  });
  const [currentTime, setCurrentTime] = useState(0);
  const [requestingWhisper, setRequestingWhisper] = useState(false);
  const [whisperMsg, setWhisperMsg] = useState(null);
  const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);

  // Locate-flow state — chunk-backed pinpoint via stored EvidenceLocation.
  // SOTA path: fetch /documents/{docId}/chunks/{chunkId}, dispatch on
  // location.kind. For media, the kind that matters is 'time_range';
  // we seek the player + highlight the segment containing time_start.
  // Falls through to the legacy substring citedMatch when locate fails
  // or returns kind=='none'. Synthetic '#read#N' ids are skipped (they
  // don't exist in exploration_chunks).
  const [chunkData, setChunkData] = useState(null);
  const [locateStatus, setLocateStatus] = useState('idle');  // idle | pending | success | fail | skip

  // Per-source visibility toggles + scroll-sync state — same pattern
  // as the inventory dropdown so panels stay in lockstep.
  const [showMerged, setShowMerged] = useState(true);
  const [showSpeech, setShowSpeech] = useState(true);
  const [showWhisperPanel, setShowWhisperPanel] = useState(true);
  const [showFrames, setShowFrames] = useState(true);
  const [showSourceToggles, setShowSourceToggles] = useState(false);
  // Which not-yet-run transcript source the user is inspecting: 'google' |
  // 'assemblyai' | null. Drives the inline "now → you'd get" upgrade panel,
  // replacing the old always-on ImproveTranscriptCard banner.
  const [activeUpgrade, setActiveUpgrade] = useState(null);
  // When the upgrade lands, auto-collapse the source panels so the
  // canonical Full Transcript dominates. User can still re-open via
  // the Sources disclosure.
  const prevHasMergedRef = useRef(false);
  const [syncedTime, setSyncedTime] = useState(0);
  const syncSourceRef = useRef(null);
  const clearSourceTimerRef = useRef(null);
  const onSyncScroll = useCallback((panelKey, t) => {
    syncSourceRef.current = panelKey;
    if (clearSourceTimerRef.current) clearTimeout(clearSourceTimerRef.current);
    clearSourceTimerRef.current = setTimeout(() => {
      syncSourceRef.current = null;
    }, 250);
    setSyncedTime(t);
  }, []);
  useEffect(() => () => {
    if (clearSourceTimerRef.current) clearTimeout(clearSourceTimerRef.current);
  }, []);

  // Eagerly start loading hls.js for video files (preloaded in index.html, so this is ~instant)
  useEffect(() => { if (isVideo) loadHls().catch(() => {}); }, [isVideo]);

  const segments = useMemo(() => textData?.transcript_segments || [], [textData]);
  const frameDescs = useMemo(() => textData?.frame_descriptions || [], [textData]);
  const whisperSegments = useMemo(() => textData?.whisper_segments || [], [textData]);
  const merged = useMemo(() => textData?.merged_transcript || [], [textData]);
  const transcription = textData?.transcription || '';
  const mediaIntel = textData?.media_intelligence || null;
  const personEntities = useMemo(() =>
    (mediaIntel?.entities || [])
      .filter(e => (e?.entity_type || '').toLowerCase() === 'person_name')
      .map(e => e.text),
    [mediaIntel],
  );
  const [speakerLabels, setSpeakerLabels] = useState(textData?.speaker_labels || {});
  useEffect(() => { setSpeakerLabels(textData?.speaker_labels || {}); }, [textData]);

  const hasSegments = segments.length > 0;
  const hasTimestamps = hasSegments && segments.some(s => s.start > 0 || s.end > 0);
  const hasFrameDescs = frameDescs.length > 0;
  const hasWhisper = whisperSegments.length > 0;
  const hasMerged = merged.length > 0;
  const hasTranscript = hasMerged || hasSegments || transcription || hasFrameDescs || hasWhisper || textLoading;

  // ── Transcript-source upgrade state (source rail, not a top banner) ──────
  // Whether each upgrade is already satisfied (chip hidden once done).
  const coveragePct = Math.round(textData?.extraction_coverage ?? 0);
  const fullCoverageDone = coveragePct >= 100;
  const aiDone = hasMerged;
  // "Speech exists to transcribe" — a silent clip (visual-only) shouldn't be
  // sold a speech transcript. Any spoken-word evidence counts.
  const hasSpeech = hasSegments || hasWhisper || hasMerged || !!transcription;
  // Live "what you have now" line for the comparison panel.
  const transcriptNowSummary = (() => {
    if (hasMerged) {
      const prov = mediaIntel?.provider_label || mediaIntel?.provider || 'AI';
      return `${prov} full transcript${Object.keys(speakerLabels || {}).length ? ' · speaker labels' : ''}`;
    }
    if (hasSegments) return `Google Cloud Speech · ${coveragePct}% coverage · no speaker labels`;
    if (transcription) return 'Basic transcript · no timestamps';
    return hasFrameDescs ? 'No spoken-word transcript yet — visual timeline only' : 'No transcript yet';
  })();
  const upgradeRunning = {
    google: upgrade.transcribing,
    assemblyai: upgradeJobStatus === 'processing' || upgradeJobStatus === 'queued',
  };

  const mediaTag = isVideo ? 'video' : 'audio';

  // Load media URL (skip if transcode is pending/failed — we already know from metadata)
  useEffect(() => {
    if (source === 'transcode_pending' || source === 'transcode_failed') return;
    let cancelled = false;
    (async () => {
      try {
        const d = await api.get(`/documents/${documentId}/media-url`);
        if (cancelled) return;
        if (d.source === 'transcode_pending' || d.source === 'transcode_failed') {
          setSource(d.source);
          setStatusMessage(d.message || 'Preparing playback… this will start automatically when ready.');
          return;
        }
        let url = d.url;
        // For backend-served media URLs the <video>/<audio> element can't
        // send an Authorization header. Mint a short-lived, document-scoped
        // viewer token instead of leaking the Firebase ID token into the URL.
        if (url && url.startsWith('/api/')) {
          try {
            const vt = await mintViewerToken(documentId);
            if (cancelled) return;
            if (vt) {
              const sep = url.includes('?') ? '&' : '?';
              url += `${sep}vt=${encodeURIComponent(vt)}`;
            }
          } catch {
            // If mint fails the media element will 401 — surface that
            // through normal error handling rather than leaking the
            // Firebase token as a fallback.
          }
        }
        setMediaUrl(url);
        setMimeType(d.mime_type || null);
        setSource(d.source || null);
        logMediaFetchStarted({ documentId, url_source: d.source || 'unknown' });
      } catch (e) {
        if (cancelled) return;
        setErr(e.message);
        logMediaFetchFailed({
          documentId,
          url_source: 'media_url_failed',
          error_class: e?.name || 'Error',
          error_message: e?.message,
        });
      }
    })();
    return () => { cancelled = true; };
  }, [documentId, source]);

  // Auto-recover from the "preparing playback" state: while a transcode is in
  // flight, poll media-url and switch to the player the moment the variant is
  // ready — no manual "check back". Matters most for a freshly-rendered
  // redacted copy opened from its completion toast (Background Jobs UX).
  useEffect(() => {
    if (source !== 'transcode_pending') return undefined;
    let cancelled = false;
    const id = setInterval(async () => {
      try {
        const d = await api.get(`/documents/${documentId}/media-url`);
        if (cancelled) return;
        if (d.source === 'transcode_pending') return; // still preparing
        if (d.source === 'transcode_failed') {
          setSource('transcode_failed');
          setStatusMessage(d.message || 'Video processing failed.');
          return;
        }
        let url = d.url;
        if (url && url.startsWith('/api/')) {
          try {
            const vt = await mintViewerToken(documentId);
            if (cancelled) return;
            if (vt) url += `${url.includes('?') ? '&' : '?'}vt=${encodeURIComponent(vt)}`;
          } catch { /* media element will surface a 401 */ }
        }
        if (cancelled) return;
        setMediaUrl(url);
        setMimeType(d.mime_type || null);
        setSource(d.source || null); // flips out of pending → player renders
      } catch { /* transient — keep polling */ }
    }, 6000);
    return () => { cancelled = true; clearInterval(id); };
  }, [documentId, source]);

  // HLS setup
  useEffect(() => {
    if (source !== 'hls' || !mediaUrl || !mediaRef.current) return;
    let hls = null;
    setHlsLoading(true);

    loadHls().then(Hls => {
      if (!mediaRef.current) return;
      if (Hls.isSupported()) {
        hls = new Hls({
          xhrSetup: (xhr, url) => {
            if (url.startsWith('/api/') || url.includes(window.location.host)) {
              const tk = getToken();
              if (tk && tk !== 'dev-mode') xhr.setRequestHeader('Authorization', `Bearer ${tk}`);
            }
          },
        });
        hls.loadSource(mediaUrl);
        hls.attachMedia(mediaRef.current);
        hls.on(Hls.Events.MANIFEST_PARSED, () => setHlsLoading(false));
        hls.on(Hls.Events.ERROR, (_event, data) => {
          if (data.fatal) {
            if (data.type === Hls.ErrorTypes.NETWORK_ERROR) hls.startLoad();
            else if (data.type === Hls.ErrorTypes.MEDIA_ERROR) hls.recoverMediaError();
            else { setHlsLoading(false); setPlayErr(true); }
          }
        });
        hlsRef.current = hls;
      } else if (mediaRef.current.canPlayType('application/vnd.apple.mpegurl')) {
        mediaRef.current.src = mediaUrl;
        setHlsLoading(false);
      } else {
        setErr('HLS not supported');
        setHlsLoading(false);
      }
    }).catch(e => { setErr('Failed to load HLS: ' + e.message); setHlsLoading(false); });

    return () => { if (hls) { hls.destroy(); hlsRef.current = null; } };
  }, [source, mediaUrl]);

  const handleTimeUpdate = useCallback((e) => setCurrentTime(e.target.currentTime), []);

  // ── Redaction Studio (flag-gated — hook reports enabled=false and the
  // studio renders nothing when av_redaction is off) ──────────────────
  const redact = useRedactions({ caseId, documentId, mediaRef });
  // Speech spans tint the waveform strip so silent gaps (where
  // un-transcribed sounds hide) are visible. Best timestamped source
  // wins: merged turns > speech segments > whisper lines.
  const speechSpans = useMemo(() => {
    const from = (arr) => arr.map((seg, i) => ({
      start: seg.start ?? 0,
      end: seg.end ?? arr[i + 1]?.start ?? (seg.start ?? 0) + 4,
      text: seg.text || '',
    }));
    if (merged.length) return from(merged);
    if (segments.length) return from(segments);
    return [];
  }, [merged, segments]);
  const onRedactRange = useCallback((start, end, label) => {
    redact.addRange(start, end, label);
  }, [redact.addRange]);  // eslint-disable-line react-hooks/exhaustive-deps

  // ── Excerpt Studio (flag-gated — hook reports enabled=false and the
  // studio renders nothing when media_excerpts is off) ─────────────────
  const excerpt = useExcerpt({ mediaRef });
  const onExcerptRange = useCallback((start, end) => {
    excerpt.markFromTranscript(start, end);
  }, [excerpt.markFromTranscript]);  // eslint-disable-line react-hooks/exhaustive-deps

  const seekTo = useCallback((time) => {
    if (mediaRef.current) {
      mediaRef.current.currentTime = time;
      if (mediaRef.current.paused) mediaRef.current.play();
    }
  }, []);

  // Apply startAt once metadata is loaded — seekable ranges aren't valid before then.
  const startAppliedRef = useRef(false);
  useEffect(() => {
    if (!startAt || startAppliedRef.current) return;
    const el = mediaRef.current;
    if (!el) return;
    const apply = () => {
      if (startAppliedRef.current) return;
      startAppliedRef.current = true;
      try { el.currentTime = startAt; el.play().catch(() => {}); } catch { /* ignore */ }
    };
    if (el.readyState >= 1) { apply(); return; }
    el.addEventListener('loadedmetadata', apply, { once: true });
    return () => el.removeEventListener('loadedmetadata', apply);
  }, [startAt, mediaUrl, source]);

  // Group whisper word-segments into ~10-word clickable lines. w0/n
  // carry each line's global word offsets so redaction mode can render
  // the words individually for word-level drag marking.
  const whisperLines = useMemo(() => {
    if (!hasWhisper) return [];
    const out = [];
    let words = [];
    let lineStart = null;
    let w0 = 0;
    whisperSegments.forEach((seg, i) => {
      if (lineStart === null) { lineStart = seg.start; w0 = i; }
      words.push(seg.text);
      if (words.length >= 10 || i === whisperSegments.length - 1) {
        out.push({ start: lineStart, text: words.join(' '), w0, n: words.length });
        words = [];
        lineStart = null;
      }
    });
    return out;
  }, [whisperSegments, hasWhisper]);

  // Word-level marking: drag across whisper words → exact spoken range.
  const onRedactWordSpan = useCallback((i0, i1) => {
    const ws = whisperSegments;
    if (!ws[i0]) return;
    const start = Math.max(0, (ws[i0].start ?? 0) - 0.1);
    const end = (ws[i1]?.end ?? ws[i1 + 1]?.start ?? (ws[i1]?.start ?? 0) + 0.5) + 0.1;
    const text = ws.slice(i0, i1 + 1).map(w => w.text).join(' ');
    redact.addRange(start, end, text.slice(0, 60));
  }, [whisperSegments, redact.addRange]);  // eslint-disable-line react-hooks/exhaustive-deps

  const activeIdx = useMemo(() => {
    if (!hasTimestamps) return -1;
    for (let i = segments.length - 1; i >= 0; i--) {
      if (currentTime >= segments[i].start) return i;
    }
    return -1;
  }, [segments, currentTime, hasTimestamps]);

  const activeWhisperIdx = useMemo(() => {
    if (!whisperLines.length) return -1;
    for (let i = whisperLines.length - 1; i >= 0; i--) {
      if (currentTime >= whisperLines[i].start) return i;
    }
    return -1;
  }, [whisperLines, currentTime]);

  const activeFrameIdx = useMemo(() => {
    if (!hasFrameDescs) return -1;
    for (let i = frameDescs.length - 1; i >= 0; i--) {
      if (currentTime >= frameDescs[i].start) return i;
    }
    return -1;
  }, [frameDescs, currentTime, hasFrameDescs]);

  const activeMergedIdx = useMemo(() => {
    if (!hasMerged) return -1;
    for (let i = merged.length - 1; i >= 0; i--) {
      if (currentTime >= (merged[i].start ?? 0)) return i;
    }
    return -1;
  }, [merged, currentTime, hasMerged]);

  // Cited-snippet match — find the first segment whose text contains the
  // verified citation snippet. Used to outline the segment in the
  // transcript panels so the user can see exactly what was cited.
  // Includes frame_descriptions: for .mov docs, the cited support_quote
  // is often a visual scene description (e.g. timestamp overlays) that
  // only exists in the Visual Timeline panel.
  const citedMatch = useMemo(() => {
    const empty = { merged: -1, speech: -1, whisper: -1, frames: -1 };
    if (!query) return empty;
    const needle = query.replace(/\s+/g, ' ').trim().toLowerCase();
    if (needle.length < 2) return empty;
    const findIn = (arr) => {
      for (let i = 0; i < arr.length; i++) {
        const t = (arr[i]?.text || '').replace(/\s+/g, ' ').toLowerCase();
        if (t.includes(needle)) return i;
      }
      return -1;
    };
    return {
      merged: hasMerged ? findIn(merged) : -1,
      speech: hasSegments ? findIn(segments) : -1,
      whisper: hasWhisper ? findIn(whisperLines) : -1,
      frames: hasFrameDescs ? findIn(frameDescs) : -1,
    };
  }, [query, merged, segments, whisperLines, frameDescs, hasMerged, hasSegments, hasWhisper, hasFrameDescs]);

  // ── Locate flow (SOTA chunk-backed pinpoint) ──────────────────────
  // Mirrors PdfPreview's pattern: when a real chunk_id is in the URL,
  // resolve it to an EvidenceLocation server-side and dispatch.
  // ``#read#N`` is a synthetic id from the read_document_text agent
  // tool; those rows don't exist in exploration_chunks so calling the
  // locate endpoint would 404. Skip the fetch and fall through to the
  // substring path for those.
  const isSyntheticChunkId = !!chunkId && chunkId.includes('#read#');
  const canLocate = !!chunkId && !isSyntheticChunkId && !!documentId;

  useEffect(() => {
    if (!chunkId || !documentId) return;
    if (isSyntheticChunkId) {
      logLocateFail({ documentId, chunkId, reason: 'synthetic_id' });
      setLocateStatus('skip');
      return;
    }
    let cancelled = false;
    logLocateAttempt({ documentId, chunkId, has_query: !!query });
    setLocateStatus('pending');
    api.get(`/documents/${documentId}/chunks/${chunkId}`)
      .then(data => {
        if (cancelled) return;
        const kind = data?.location?.kind || 'none';
        setChunkData(data);
        if (kind === 'time_range') {
          setLocateStatus('success');
          logLocateSuccess({
            documentId, chunkId,
            location_kind: kind,
            time_start: data.location.time_start,
            time_end: data.location.time_end,
          });
        } else {
          // Chunk exists but its location isn't usable for media
          // (kind='none' or unexpected kind like bbox/char_range on
          // a media doc). Substring fallback takes over.
          setLocateStatus('fail');
          logLocateFail({ documentId, chunkId, reason: 'no_location', location_kind: kind });
        }
      })
      .catch(err => {
        if (cancelled) return;
        setLocateStatus('fail');
        const status = err?.status || err?.response?.status;
        logLocateFail({
          documentId, chunkId,
          reason: status === 404 ? 'chunk_not_found' : 'network',
          error: err?.message,
        });
      });
    return () => { cancelled = true; };
  }, [chunkId, documentId, isSyntheticChunkId, query]);

  // Time-anchor `findAt` — locate per-panel segment containing time t.
  // Latest segment whose start <= t (matching the existing activeIdx
  // logic). Shared by both locateCited and markerCited below.
  const citedByTime = useCallback((t) => {
    const empty = { merged: -1, speech: -1, whisper: -1, frames: -1 };
    if (!Number.isFinite(t)) return empty;
    const findAt = (arr) => {
      for (let i = arr.length - 1; i >= 0; i--) {
        const s = arr[i]?.start ?? 0;
        if (t >= s) return i;
      }
      return -1;
    };
    return {
      merged: hasMerged ? findAt(merged) : -1,
      speech: hasSegments ? findAt(segments) : -1,
      whisper: hasWhisper ? findAt(whisperLines) : -1,
      frames: hasFrameDescs ? findAt(frameDescs) : -1,
    };
  }, [merged, segments, whisperLines, frameDescs, hasMerged, hasSegments, hasWhisper, hasFrameDescs]);

  // Locate-derived cited index — uses chunkData.location.time_start
  // when the server resolved a time_range for this chunk. Returns -1
  // per panel otherwise so the cascade in effectiveCited falls through.
  const locateCited = useMemo(() => {
    if (!chunkData?.location || chunkData.location.kind !== 'time_range') {
      return { merged: -1, speech: -1, whisper: -1, frames: -1 };
    }
    return citedByTime(chunkData.location.time_start);
  }, [chunkData, citedByTime]);

  // Marker-derived time anchor — `[HH:MM:SS]` or `[MM:SS]` bracketed
  // timestamp parsed out of the citation snippet. Grounded renderer
  // routinely emits these markers when stitching Q/A across speakers
  // (e.g. "...did you ever know X? [00:00:08] I wouldn't know.").
  // Falls back to this when locate misses, so a citation whose chunk
  // has no stored time_range still pins to the right segment.
  // Picks the FIRST marker — the typical Q/A pattern puts the anchor
  // at the answer boundary, which is close enough to land in the
  // segment that contains the cited content.
  const queryTimeAnchor = useMemo(() => {
    if (!query) return null;
    const m = query.match(/\[(?:(\d{1,2}):)?(\d{1,2}):(\d{2})\]/);
    if (!m) return null;
    const hours = m[1] ? parseInt(m[1], 10) : 0;
    const minutes = parseInt(m[2], 10);
    const seconds = parseInt(m[3], 10);
    return hours * 3600 + minutes * 60 + seconds;
  }, [query]);

  const markerCited = useMemo(() => {
    if (queryTimeAnchor == null) {
      return { merged: -1, speech: -1, whisper: -1, frames: -1 };
    }
    return citedByTime(queryTimeAnchor);
  }, [queryTimeAnchor, citedByTime]);

  // Effective cited index — priority chain per panel:
  //   1. locate (SOTA: server-resolved time_range from chunk store)
  //   2. marker (parsed [mm:ss] in citation snippet)
  //   3. substring (legacy fuzzy text match)
  // Per-panel rather than all-or-nothing because different panels may
  // resolve different time ranges (e.g. a speech-derived chunk's
  // time_start lands inside a speech segment but not a Visual Timeline
  // keyframe).
  const effectiveCited = useMemo(() => {
    const pick = (panel) => {
      if (locateCited[panel] >= 0) return locateCited[panel];
      if (markerCited[panel] >= 0) return markerCited[panel];
      return citedMatch[panel];
    };
    return {
      merged: pick('merged'),
      speech: pick('speech'),
      whisper: pick('whisper'),
      frames: pick('frames'),
    };
  }, [locateCited, markerCited, citedMatch]);

  // Seek player to time_start once the player is ready. startAppliedRef
  // below (the existing startAt path) uses the same gate; we re-use
  // that flag to avoid double-seeking when both startAt and a locate
  // result are present.
  const locateSeekAppliedRef = useRef(false);
  useEffect(() => {
    if (locateStatus !== 'success') return;
    if (!chunkData?.location || chunkData.location.kind !== 'time_range') return;
    const t = chunkData.location.time_start;
    if (!Number.isFinite(t)) return;
    const el = mediaRef.current;
    if (!el) return;
    if (locateSeekAppliedRef.current) return;
    const apply = () => {
      if (locateSeekAppliedRef.current) return;
      locateSeekAppliedRef.current = true;
      try { el.currentTime = t; } catch { /* ignore */ }
    };
    if (el.readyState >= 1) { apply(); return; }
    el.addEventListener('loadedmetadata', apply, { once: true });
    return () => el.removeEventListener('loadedmetadata', apply);
  }, [locateStatus, chunkData, mediaUrl, source]);

  // Surface the chunk's verbatim text as the resolved snippet — same
  // contract DocumentPreview uses. The EvidenceCallout will then quote
  // the chunk text rather than the URL ``q=`` param, which is more
  // authoritative.
  useEffect(() => {
    if (locateStatus !== 'success') return;
    if (!chunkData?.text || !onResolvedSnippet) return;
    onResolvedSnippet(chunkData.text);
  }, [locateStatus, chunkData, onResolvedSnippet]);

  // Report pinpoint status up to the EvidenceCallout. With the locate
  // flow in play, we also wait for any in-flight locate to complete so
  // we don't flash 'no-location' before the chunk resolves. Falls back
  // to substring citedMatch (incl. frame_descriptions) when locate
  // fails / is skipped.
  //
  // ``found`` must reflect what the USER sees, not what we computed.
  // When ``hasMerged`` flips true the source panels auto-collapse, so
  // a hit on frames/whisper/speech is invisible — locate's ``findAt``
  // returns ≥0 for almost any time_start on those panels, which would
  // otherwise overclaim "● Highlighted on document" while the only
  // rendered panel (merged) has no marked row.
  useEffect(() => {
    if (!onHighlightStatus) return;
    if (!query && !canLocate) return;
    if (textLoading) return;
    if (locateStatus === 'pending') return;
    const visibleFound = (
      (hasMerged && showMerged && effectiveCited.merged >= 0) ||
      (hasSegments && showSpeech && effectiveCited.speech >= 0) ||
      (hasWhisper && showWhisperPanel && effectiveCited.whisper >= 0) ||
      (hasFrameDescs && showFrames && effectiveCited.frames >= 0)
    );
    onHighlightStatus(visibleFound ? 'located' : 'no-location');
    // Mirrors PdfPreview's emission so cross-format anchoring rates
    // are comparable in viewer_events / fetch_viewer_events.py.
    // ``method`` reflects which step in effectiveCited's cascade
    // contributed the winning index (locate > marker > substring).
    const hasLocateHit = (
      locateCited.merged >= 0 || locateCited.speech >= 0 ||
      locateCited.whisper >= 0 || locateCited.frames >= 0
    );
    const hasMarkerHit = (
      markerCited.merged >= 0 || markerCited.speech >= 0 ||
      markerCited.whisper >= 0 || markerCited.frames >= 0
    );
    const method = hasLocateHit ? 'locate' : (hasMarkerHit ? 'marker' : 'substring');
    logHighlight({
      documentId, chunkId,
      kind: 'media',
      located: visibleFound,
      method,
      panels: {
        merged: effectiveCited.merged,
        speech: effectiveCited.speech,
        whisper: effectiveCited.whisper,
        frames: effectiveCited.frames,
      },
    });
  }, [
    query, canLocate, effectiveCited, textLoading, onHighlightStatus,
    locateStatus, documentId, chunkId,
    hasMerged, hasSegments, hasWhisper, hasFrameDescs,
    showMerged, showSpeech, showWhisperPanel, showFrames,
    locateCited, markerCited,
  ]);

  // When the upgrade transcript lands (hasMerged flips false → true),
  // auto-collapse the legacy source panels so the canonical Full
  // Transcript dominates. User can re-open them via the Sources
  // disclosure if they want to compare.
  //
  // Inverse case: when the doc has NO merged transcript and the data
  // has loaded, the source panels ARE the primary content — expand
  // the Sources disclosure and re-assert the chips active so the user
  // sees the Visual / Speech / Whisper transcript directly instead of
  // having to dig two clicks deep. Only runs once per textData load
  // (gated on textLoading flipping false) so it doesn't fight user
  // toggle clicks after the fact.
  const textDataSettledRef = useRef(false);
  useEffect(() => {
    if (!prevHasMergedRef.current && hasMerged) {
      setShowSpeech(false);
      setShowWhisperPanel(false);
      setShowFrames(false);
    }
    prevHasMergedRef.current = hasMerged;
  }, [hasMerged]);
  useEffect(() => {
    if (textLoading) {
      textDataSettledRef.current = false;
      return;
    }
    if (textDataSettledRef.current) return;
    textDataSettledRef.current = true;
    if (!hasMerged && (hasSegments || hasWhisper || hasFrameDescs)) {
      setShowSourceToggles(true);
      if (hasSegments) setShowSpeech(true);
      if (hasWhisper) setShowWhisperPanel(true);
      if (hasFrameDescs) setShowFrames(true);
    }
  }, [textLoading, hasMerged, hasSegments, hasWhisper, hasFrameDescs]);

  async function handleRequestWhisper() {
    setRequestingWhisper(true);
    setWhisperMsg(null);
    try {
      const est = await api.post(`/processing/documents/${documentId}/transcribe-whisper?estimate=true`);
      if (est.whisper_status === 'completed') {
        setWhisperMsg('Whisper transcript already exists. Reload to view it.');
        setRequestingWhisper(false);
        return;
      }
      const dur = est.duration_seconds || 0;
      const costCents = est.estimated_cost_cents || 0;
      const ok = window.confirm(
        `Request high-accuracy Whisper transcript?\n` +
        `Duration: ${Math.round(dur)}s (~${Math.round(dur / 60)} min)\n` +
        `Estimated cost: $${(costCents / 100).toFixed(2)}\n\n` +
        `⚠️ DATA BOUNDARY WARNING\n` +
        `This will send the audio file to OpenAI (Whisper API), outside\n` +
        `Google Assured Workloads.\n\nProceed?`
      );
      if (!ok) { setRequestingWhisper(false); return; }
      const result = await api.post(`/processing/documents/${documentId}/transcribe-whisper?estimate=false`);
      if (result.success) {
        setWhisperMsg('Whisper transcription complete. Reload to view.');
      } else {
        setWhisperMsg(`Failed: ${result.error || 'Unknown error'}`);
      }
    } catch (err) {
      setWhisperMsg(`Error: ${err.message}`);
    } finally {
      setRequestingWhisper(false);
    }
  }

  // Escape exits draw-on-frame mode. Capture phase so the viewer's own
  // window-level ESC-to-close handler (bubble) never sees the event —
  // otherwise cancelling a box draw would close the whole viewer.
  useEffect(() => {
    if (!redact.drawMode) return;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        redact.setDrawMode(null);
      }
    };
    window.addEventListener('keydown', onKey, { capture: true });
    return () => window.removeEventListener('keydown', onKey, { capture: true });
  }, [redact.drawMode]);  // eslint-disable-line react-hooks/exhaustive-deps

  // Visual-redaction layer over the <video>: live blur/black preview +
  // draw-to-capture. Audio docs never mount it.
  const videoOverlay = (isVideo && redact.enabled) ? (
    <VideoRedactionOverlay
      mediaRef={mediaRef}
      items={redact.items}
      currentTime={currentTime}
      previewOn={redact.preview}
      drawing={!!redact.drawMode}
      drawStyle={redact.drawMode?.style}
      onCapture={(bbox) => redact.addRegion(bbox, redact.drawMode?.style || 'blur')}
    />
  ) : null;

  // Render media element
  function renderPlayer() {
    if (err) return <div className="text-muted">Media unavailable: {err}</div>;
    if (source === 'transcode_pending') {
      return <div className="dv-status-msg">{statusMessage || 'Preparing playback… this will start automatically when ready.'}</div>;
    }
    if (source === 'transcode_failed') {
      return <div className="dv-status-msg dv-status-error">{statusMessage || 'Video processing failed.'}</div>;
    }
    if (!mediaUrl) return <div className="dd-loading">Loading player…</div>;
    if (playErr) return <div className="dv-status-msg">Playback failed — this {(mimeType || '').split('/')[0] || 'media'} format could not be decoded by your browser.</div>;

    if (source === 'hls') {
      return (
        <div style={{ position: 'relative' }}>
          <video ref={mediaRef} controls style={{ width: '100%', maxHeight: '60vh', background: '#000' }} onTimeUpdate={handleTimeUpdate} />
          {videoOverlay}
          {hlsLoading && (
            <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.6)', color: '#ccc' }}>
              Loading stream…
            </div>
          )}
        </div>
      );
    }

    const effectiveTag = mimeType?.startsWith('video/') ? 'video' : mimeType?.startsWith('audio/') ? 'audio' : mediaTag;

    const onMediaError = (e) => {
      setPlayErr(true);
      // MediaError.code: 1=MEDIA_ERR_ABORTED, 2=NETWORK, 3=DECODE, 4=SRC_NOT_SUPPORTED
      const me = e?.target?.error;
      logMediaFetchFailed({
        documentId,
        url_source: source || 'unknown',
        error_class: me ? `media_error_${me.code}` : 'media_error',
        error_message: me?.message,
      });
    };

    if (effectiveTag === 'video') {
      return (
        <div style={{ position: 'relative' }}>
          <video ref={mediaRef} controls src={mediaUrl} style={{ width: '100%', maxHeight: '60vh', background: '#000', display: 'block' }}
            onTimeUpdate={handleTimeUpdate} onError={onMediaError} />
          {videoOverlay}
        </div>
      );
    }
    return <audio ref={mediaRef} controls src={mediaUrl} style={{ width: '100%' }}
      onTimeUpdate={handleTimeUpdate} onError={onMediaError} />;
  }

  function renderTranscript() {
    if (textLoading) {
      return (
        <div style={{ padding: 20, textAlign: 'center' }}>
          <div className="dv-loading-bar" style={{ marginBottom: 8 }} />
          <div className="text-muted" style={{ fontSize: 12 }}>Loading transcript…</div>
        </div>
      );
    }

    const visibleCount = (hasMerged && showMerged ? 1 : 0)
      + (hasSegments && showSpeech ? 1 : 0)
      + (hasWhisper && showWhisperPanel ? 1 : 0)
      + (hasFrameDescs && showFrames ? 1 : 0);

    return (
      <>
        {/* Source rail — the single transcript-source switcher. Existing
            sources (Full Transcript / Speech / Whisper / Visual) plus the two
            NOT-yet-run upgrades (Full coverage, AI transcript) as selectable
            chips. The upgrade prompt is pull-based: it appears below only when
            its chip is selected (no always-on banner). */}
        {(hasMerged || hasSegments || hasWhisper || hasFrameDescs
          || !fullCoverageDone || !aiDone) && (
          <div className="dv-transcript-toggles">
            {hasMerged && (
              <button
                className={`dv-transcript-toggle ${showMerged ? 'dv-transcript-toggle--active' : ''}`}
                onClick={() => setShowMerged(!showMerged)}
                type="button"
              >
                <span>✨ Full Transcript</span>
                <span className="dv-transcript-toggle-count">{merged.length}</span>
              </button>
            )}
            {(hasSegments || hasWhisper || hasFrameDescs) && (
              <button
                className="dv-transcript-toggle"
                onClick={() => setShowSourceToggles(!showSourceToggles)}
                type="button"
                title="Show / hide the source transcripts"
              >
                <span>{showSourceToggles ? '▾' : '▸'} Sources</span>
              </button>
            )}
            {showSourceToggles && hasSegments && (
              <button
                className={`dv-transcript-toggle ${showSpeech ? 'dv-transcript-toggle--active' : ''}`}
                onClick={() => setShowSpeech(!showSpeech)}
                type="button"
              >
                <span>📝 Speech</span>
                <span className="dv-transcript-toggle-count">{segments.length}</span>
              </button>
            )}
            {showSourceToggles && hasWhisper && (
              <button
                className={`dv-transcript-toggle ${showWhisperPanel ? 'dv-transcript-toggle--active' : ''}`}
                onClick={() => setShowWhisperPanel(!showWhisperPanel)}
                type="button"
              >
                <span>🎙 Whisper</span>
                <span className="dv-transcript-toggle-count">{whisperSegments.length} words</span>
              </button>
            )}
            {showSourceToggles && hasFrameDescs && (
              <button
                className={`dv-transcript-toggle ${showFrames ? 'dv-transcript-toggle--active' : ''}`}
                onClick={() => setShowFrames(!showFrames)}
                type="button"
              >
                <span>🎥 Visual</span>
                <span className="dv-transcript-toggle-count">{frameDescs.length}</span>
              </button>
            )}
            {/* Not-yet-run upgrades — selecting one opens its comparison panel
                below; disabled (with a reason) when there's no speech to add. */}
            {!fullCoverageDone && (
              <button
                className={`dv-transcript-toggle dv-transcript-toggle--upgrade ${activeUpgrade === 'google' ? 'dv-transcript-toggle--active' : ''}`}
                onClick={() => hasSpeech && setActiveUpgrade(activeUpgrade === 'google' ? null : 'google')}
                disabled={!hasSpeech}
                type="button"
                title={hasSpeech ? 'Transcribe the full audio with Google' : 'No speech detected — nothing to transcribe'}
              >
                <span>☁ Full coverage</span>
                {upgradeRunning.google && <span className="dv-transcript-toggle-count">…</span>}
              </button>
            )}
            {!aiDone && (
              <button
                className={`dv-transcript-toggle dv-transcript-toggle--upgrade ${activeUpgrade === 'assemblyai' ? 'dv-transcript-toggle--active' : ''}`}
                onClick={() => hasSpeech && setActiveUpgrade(activeUpgrade === 'assemblyai' ? null : 'assemblyai')}
                disabled={!hasSpeech}
                type="button"
                title={hasSpeech ? 'Upgrade with AssemblyAI (speaker labels, chapters, entities)' : 'No speech detected — nothing to transcribe'}
              >
                <span>✨ AI transcript</span>
                {upgradeRunning.assemblyai && <span className="dv-transcript-toggle-count">…</span>}
              </button>
            )}
          </div>
        )}

        {/* Inline "now → you'd get" comparison for the selected upgrade. */}
        {activeUpgrade && (
          <TranscriptUpgradePanel
            provider={activeUpgrade}
            nowSummary={transcriptNowSummary}
            hasSpeech={hasSpeech}
            running={!!upgradeRunning[activeUpgrade]}
            onGenerate={() => {
              if (activeUpgrade === 'google') upgrade.handleFullTranscribe();
              else setUpgradeModalOpen(true);
            }}
            onClose={() => setActiveUpgrade(null)}
          />
        )}

        {/* Side-by-side panels */}
        {visibleCount > 0 && (
          <div className="dv-transcript-panels-row">
            {hasMerged && showMerged && (
              <ViewerMergedPanel
                lines={merged}
                activeIdx={activeMergedIdx}
                citedIdx={effectiveCited.merged}
                panelKey="merged" syncedTime={syncedTime}
                syncSourceRef={syncSourceRef} onSyncScroll={onSyncScroll}
                onSeek={seekTo}
                providerLabel={mediaIntel?.provider_label || mediaIntel?.provider}
                speakerLabels={speakerLabels}
                personEntities={personEntities}
                docId={documentId}
                onLabelsUpdated={(next) => setSpeakerLabels(next)}
                redactionMode={redact.mode}
                onRedactRange={onRedactRange}
                excerptMode={excerpt.mode}
                onExcerptRange={onExcerptRange}
              />
            )}
            {hasSegments && showSpeech && (
              <ViewerSegmentPanel
                title="Speech Transcript" lines={segments} activeIdx={activeIdx}
                citedIdx={effectiveCited.speech}
                panelKey="speech" syncedTime={syncedTime}
                syncSourceRef={syncSourceRef} onSyncScroll={onSyncScroll}
                onSeek={seekTo}
                redactionMode={redact.mode}
                onRedactRange={onRedactRange}
                excerptMode={excerpt.mode}
                onExcerptRange={onExcerptRange}
              />
            )}
            {hasWhisper && showWhisperPanel && (
              <ViewerSegmentPanel
                title="Whisper Transcript"
                subtitle={`word-for-word · ${whisperSegments.length} words`}
                lines={whisperLines} activeIdx={activeWhisperIdx}
                citedIdx={effectiveCited.whisper}
                panelKey="whisper" syncedTime={syncedTime}
                syncSourceRef={syncSourceRef} onSyncScroll={onSyncScroll}
                onSeek={seekTo}
                redactionMode={redact.mode}
                onRedactRange={onRedactRange}
                excerptMode={excerpt.mode}
                onExcerptRange={onExcerptRange}
                words={whisperSegments}
                onRedactWordSpan={onRedactWordSpan}
              />
            )}
            {hasFrameDescs && showFrames && (
              <ViewerSegmentPanel
                title="Visual Timeline" lines={frameDescs} activeIdx={activeFrameIdx}
                citedIdx={effectiveCited.frames}
                panelKey="frames" syncedTime={syncedTime}
                syncSourceRef={syncSourceRef} onSyncScroll={onSyncScroll}
                onSeek={seekTo}
              />
            )}
          </div>
        )}
        {visibleCount === 0 && (hasMerged || hasSegments || hasWhisper || hasFrameDescs) && (
          <div className="text-muted" style={{ padding: 20, textAlign: 'center', fontSize: 12 }}>
            Use the toggles above to show a transcript source.
          </div>
        )}
        {!hasSegments && !hasWhisper && !hasFrameDescs && transcription && (
          <pre className="dv-plain-transcript">{transcription}</pre>
        )}
        {!hasSegments && !hasWhisper && !hasFrameDescs && !transcription && (
          <div className="text-muted" style={{ padding: 20 }}>No transcript available.</div>
        )}
      </>
    );
  }

  return (
    <div className="dv-media-layout">
      <div className="dv-media-player">
        {renderPlayer()}
        {/* Redaction Studio docks under the player (inside the sticky
            left column on wide viewports — adding it as a sibling of
            dv-media-player would become a third flex column and crush
            the transcript panels). */}
        <RedactionStudio
          redact={redact}
          caseId={caseId}
          documentId={documentId}
          currentTime={currentTime}
          onSeek={seekTo}
          speechSpans={speechSpans}
          isVideo={isVideo}
          redactionOf={redactionOf}
          onOpenDocument={onOpenDocument}
        />
        {/* Excerpt Studio — same dock. On an excerpt document it shows the
            provenance line + "Open the original here" instead of the tools. */}
        <ExcerptStudio
          excerpt={excerpt}
          caseId={caseId}
          documentId={documentId}
          currentTime={currentTime}
          onSeek={seekTo}
          isVideo={isVideo}
          excerptRecord={excerptRecord}
          onOpenDocument={onOpenDocument}
        />
      </div>
      {hasTranscript && (
        <div className="dv-transcript-section">
          {renderTranscript()}
        </div>
      )}
      <UpgradeTranscriptModal
        open={upgradeModalOpen}
        onClose={() => setUpgradeModalOpen(false)}
        scope="document"
        targetId={documentId}
        onComplete={() => onTranscriptChanged?.()}
      />
    </div>
  );
}

// Canonical chat-log panel for the viewer's Full Transcript. Same
// scroll-lock + auto-scroll story as ViewerSegmentPanel but renders
// speaker turns instead of flat segments.
function ViewerMergedPanel({
  lines, activeIdx, citedIdx = -1, panelKey, syncedTime, syncSourceRef, onSyncScroll,
  onSeek, providerLabel,
  speakerLabels, personEntities, docId, onLabelsUpdated,
  redactionMode = false, onRedactRange = null,
  excerptMode = false, onExcerptRange = null,
}) {
  const containerRef = useRef(null);
  const isProgrammaticRef = useRef(false);
  const [openLetter, setOpenLetter] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);

  function closePopover() { setOpenLetter(null); setAnchorEl(null); }

  function setSpeakerName(letter, name) {
    const prior = { ...(speakerLabels || {}) };
    const next = { ...prior };
    if (name === '') delete next[letter];
    else next[letter] = name;
    onLabelsUpdated?.(next);  // optimistic
    closePopover();
    api.patch(`/documents/${docId}/speaker-labels`, { labels: next })
      .catch(err => {
        console.error('speaker-labels PATCH failed', err);
        onLabelsUpdated?.(prior);
      });
  }

  useEffect(() => {
    if (activeIdx == null || activeIdx < 0 || !containerRef.current) return;
    const c = containerRef.current;
    const el = c.querySelector(`[data-idx="${activeIdx}"]`);
    if (!el) return;
    isProgrammaticRef.current = true;
    el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    const tid = setTimeout(() => { isProgrammaticRef.current = false; }, 250);
    return () => clearTimeout(tid);
  }, [activeIdx]);

  // One-shot scroll to the cited segment on mount. Runs once per
  // citedIdx change so re-renders driven by playback don't re-scroll.
  const lastCitedScrollRef = useRef(-1);
  useEffect(() => {
    if (citedIdx == null || citedIdx < 0 || !containerRef.current) return;
    if (lastCitedScrollRef.current === citedIdx) return;
    const c = containerRef.current;
    const el = c.querySelector(`[data-idx="${citedIdx}"]`);
    if (!el) return;
    lastCitedScrollRef.current = citedIdx;
    isProgrammaticRef.current = true;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    const tid = setTimeout(() => { isProgrammaticRef.current = false; }, 300);
    return () => clearTimeout(tid);
  }, [citedIdx, lines]);

  useEffect(() => {
    if (!containerRef.current || !lines || lines.length === 0) return;
    if (syncSourceRef?.current === panelKey) return;
    let idx = -1;
    for (let i = 0; i < lines.length; i++) {
      if ((lines[i].start ?? 0) <= syncedTime) idx = i;
      else break;
    }
    if (idx < 0) idx = 0;
    const c = containerRef.current;
    const el = c.querySelector(`[data-idx="${idx}"]`);
    if (!el) return;
    isProgrammaticRef.current = true;
    const cRect = c.getBoundingClientRect();
    const elRect = el.getBoundingClientRect();
    c.scrollTop += elRect.top - cRect.top;
    const tid = setTimeout(() => { isProgrammaticRef.current = false; }, 80);
    return () => clearTimeout(tid);
  }, [syncedTime, lines, panelKey, syncSourceRef]);

  useEffect(() => {
    const c = containerRef.current;
    if (!c || !lines || lines.length === 0 || !onSyncScroll) return;
    const handler = () => {
      if (isProgrammaticRef.current) return;
      const cTop = c.getBoundingClientRect().top;
      let topIdx = 0;
      const segs = c.querySelectorAll('[data-idx]');
      for (let i = 0; i < segs.length; i++) {
        if (segs[i].getBoundingClientRect().bottom >= cTop + 4) {
          topIdx = parseInt(segs[i].dataset.idx, 10);
          break;
        }
      }
      const t = lines[topIdx]?.start;
      if (t == null) return;
      onSyncScroll(panelKey, t);
    };
    c.addEventListener('scroll', handler, { passive: true });
    return () => c.removeEventListener('scroll', handler);
  }, [lines, panelKey, onSyncScroll]);

  if (!lines || lines.length === 0) return null;

  return (
    <div className="dv-transcript-panel dv-transcript-panel--merged">
      <div className="dv-transcript-panel-header">
        <span>✨ Full Transcript</span>
        {providerLabel && (
          <span className="dv-transcript-panel-subtitle">via {providerLabel}</span>
        )}
      </div>
      <div className="dv-transcript-segments dv-merged-segments" ref={containerRef}>
        {lines.map((seg, i) => {
          const prevSpeaker = i > 0 ? lines[i - 1].speaker : null;
          const speakerChanged = seg.speaker !== prevSpeaker;
          const displayName = seg.speaker
            ? (speakerLabels?.[seg.speaker] || `Speaker ${seg.speaker}`)
            : null;
          return (
            <div
              key={i}
              data-idx={i}
              className={`dv-merged-line ${speakerChanged ? 'dv-merged-line--turn' : ''} ${i === activeIdx ? 'dv-merged-line--active' : ''} ${i === citedIdx ? 'dv-merged-line--cited' : ''}`}
              title={`Seek to ${fmtTime(seg.start)}`}
            >
              {speakerChanged && seg.speaker && docId && (
                <span
                  className="dv-merged-speaker"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (openLetter === seg.speaker) closePopover();
                    else { setOpenLetter(seg.speaker); setAnchorEl(e.currentTarget); }
                  }}
                  title="Click to rename this speaker"
                >
                  {displayName}
                </span>
              )}
              <div
                className="dv-merged-row"
                onClick={() => onSeek?.(seg.start)}
                style={{ cursor: 'pointer' }}
              >
                <span className="dv-seg-ts">{fmtTime(seg.start)}</span>
                <span className="dv-merged-text">{seg.text}</span>
                {redactionMode && onRedactRange && (
                  <button
                    className="rdx-seg-btn"
                    type="button"
                    title="Redact this line's audio"
                    onClick={(e) => {
                      e.stopPropagation();
                      const end = seg.end ?? lines[i + 1]?.start ?? seg.start + 5;
                      onRedactRange(seg.start ?? 0, end, (seg.text || '').slice(0, 60));
                    }}
                  >
                    ⛔
                  </button>
                )}
                {excerptMode && onExcerptRange && (
                  <button
                    className="rdx-seg-btn xpt-seg-btn"
                    type="button"
                    title="Include this line in the excerpt (first line sets the range, more lines extend it)"
                    onClick={(e) => {
                      e.stopPropagation();
                      const end = seg.end ?? lines[i + 1]?.start ?? seg.start + 5;
                      onExcerptRange(seg.start ?? 0, end);
                    }}
                  >
                    ✂
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
      {openLetter && anchorEl && (
        <SpeakerLabelPopover
          letter={openLetter}
          currentName={speakerLabels?.[openLetter] || null}
          personEntities={personEntities}
          anchorEl={anchorEl}
          onSet={(name) => setSpeakerName(openLetter, name)}
          onClose={closePopover}
        />
      )}
    </div>
  );
}

// Per-panel transcript renderer for the viewer. Owns its own scroll
// container, auto-scrolls to the active row as media plays, and
// participates in cross-panel scroll-lock when the user drags a
// scrollbar manually. A single isProgrammaticRef flag suppresses
// re-emission for both auto-scroll and sync-driven scroll.
function ViewerSegmentPanel({
  title, subtitle, lines, activeIdx, citedIdx = -1, panelKey,
  syncedTime, syncSourceRef, onSyncScroll, onSeek,
  redactionMode = false, onRedactRange = null,
  excerptMode = false, onExcerptRange = null,
  words = null, onRedactWordSpan = null,
}) {
  const containerRef = useRef(null);
  const isProgrammaticRef = useRef(false);

  // Word-level drag marking (whisper panel only — `words` provided and
  // redaction mode on): drag across words, release to redact exactly
  // that spoken span. Selection state is [loIdx, hiIdx] global indices.
  const wordDragRef = useRef(null);
  const [wordSel, setWordSel] = useState(null);
  const wordLevel = !!(redactionMode && words && onRedactWordSpan);
  useEffect(() => {
    if (!wordLevel) { setWordSel(null); wordDragRef.current = null; return; }
    const onUp = () => {
      if (wordDragRef.current == null) return;
      const sel = wordSel;
      wordDragRef.current = null;
      setWordSel(null);
      if (sel) onRedactWordSpan(sel[0], sel[1]);
    };
    window.addEventListener('mouseup', onUp);
    return () => window.removeEventListener('mouseup', onUp);
  }, [wordLevel, wordSel, onRedactWordSpan]);

  // Auto-scroll to the active row as media playback advances.
  useEffect(() => {
    if (activeIdx == null || activeIdx < 0 || !containerRef.current) return;
    const c = containerRef.current;
    const el = c.querySelector(`[data-idx="${activeIdx}"]`);
    if (!el) return;
    isProgrammaticRef.current = true;
    el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    const tid = setTimeout(() => { isProgrammaticRef.current = false; }, 250);
    return () => clearTimeout(tid);
  }, [activeIdx]);

  // One-shot scroll to the cited segment when the panel first sees a
  // citedIdx (or when citedIdx changes — e.g. a new citation deep-link).
  const lastCitedScrollRef = useRef(-1);
  useEffect(() => {
    if (citedIdx == null || citedIdx < 0 || !containerRef.current) return;
    if (lastCitedScrollRef.current === citedIdx) return;
    const c = containerRef.current;
    const el = c.querySelector(`[data-idx="${citedIdx}"]`);
    if (!el) return;
    lastCitedScrollRef.current = citedIdx;
    isProgrammaticRef.current = true;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    const tid = setTimeout(() => { isProgrammaticRef.current = false; }, 300);
    return () => clearTimeout(tid);
  }, [citedIdx, lines]);

  // Apply sync-scroll from another panel.
  useEffect(() => {
    if (!containerRef.current || !lines || lines.length === 0) return;
    if (syncSourceRef?.current === panelKey) return;
    let idx = -1;
    for (let i = 0; i < lines.length; i++) {
      if (lines[i].start <= syncedTime) idx = i;
      else break;
    }
    if (idx < 0) idx = 0;
    const c = containerRef.current;
    const el = c.querySelector(`[data-idx="${idx}"]`);
    if (!el) return;
    isProgrammaticRef.current = true;
    const cRect = c.getBoundingClientRect();
    const elRect = el.getBoundingClientRect();
    c.scrollTop += elRect.top - cRect.top;
    const tid = setTimeout(() => { isProgrammaticRef.current = false; }, 80);
    return () => clearTimeout(tid);
  }, [syncedTime, lines, panelKey, syncSourceRef]);

  // Detect user scrolls and emit upward.
  useEffect(() => {
    const c = containerRef.current;
    if (!c || !lines || lines.length === 0 || !onSyncScroll) return;
    const handler = () => {
      if (isProgrammaticRef.current) return;
      const cTop = c.getBoundingClientRect().top;
      let topIdx = 0;
      const segs = c.querySelectorAll('[data-idx]');
      for (let i = 0; i < segs.length; i++) {
        if (segs[i].getBoundingClientRect().bottom >= cTop + 4) {
          topIdx = parseInt(segs[i].dataset.idx, 10);
          break;
        }
      }
      const t = lines[topIdx]?.start;
      if (t == null) return;
      onSyncScroll(panelKey, t);
    };
    c.addEventListener('scroll', handler, { passive: true });
    return () => c.removeEventListener('scroll', handler);
  }, [lines, panelKey, onSyncScroll]);

  if (!lines || lines.length === 0) return null;
  return (
    <div className="dv-transcript-panel">
      <div className="dv-transcript-panel-header">
        <span>{title}</span>
        {subtitle && <span className="dv-transcript-panel-subtitle">{subtitle}</span>}
      </div>
      <div className="dv-transcript-segments" ref={containerRef}>
        {lines.map((seg, i) => (
          <div
            key={i}
            data-idx={i}
            className={`dv-seg ${i === activeIdx ? 'dv-seg--active' : ''} ${i === citedIdx ? 'dv-seg--cited' : ''}`}
            onClick={wordLevel ? undefined : () => onSeek?.(seg.start)}
            title={wordLevel ? undefined : `Seek to ${fmtTime(seg.start)}`}
          >
            <span
              className="dv-seg-ts"
              onClick={wordLevel ? (e) => { e.stopPropagation(); onSeek?.(seg.start); } : undefined}
              title={wordLevel ? `Seek to ${fmtTime(seg.start)}` : undefined}
            >
              {fmtTime(seg.start)}
            </span>
            {wordLevel && seg.n != null ? (
              <span className="dv-seg-txt" title="Drag across words to redact exactly that span">
                {words.slice(seg.w0, seg.w0 + seg.n).map((w, k) => {
                  const gi = seg.w0 + k;
                  const sel = wordSel && gi >= wordSel[0] && gi <= wordSel[1];
                  return (
                    <span
                      key={k}
                      className={`rdx-word ${sel ? 'rdx-word--sel' : ''}`}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        wordDragRef.current = gi;
                        setWordSel([gi, gi]);
                      }}
                      onMouseEnter={() => {
                        const a = wordDragRef.current;
                        if (a != null) setWordSel([Math.min(a, gi), Math.max(a, gi)]);
                      }}
                    >
                      {w.text}{' '}
                    </span>
                  );
                })}
              </span>
            ) : (
              <span className="dv-seg-txt">
                {seg.source === 'video' && <span className="dv-seg-src" title="Written by the video pass (Gemini), not by speech-to-text">video</span>}
                {seg.text}
              </span>
            )}
            {redactionMode && onRedactRange && (
              <button
                className="rdx-seg-btn"
                type="button"
                title="Redact this line's audio"
                onClick={(e) => {
                  e.stopPropagation();
                  const end = seg.end ?? lines[i + 1]?.start ?? seg.start + 5;
                  onRedactRange(seg.start ?? 0, end, (seg.text || '').slice(0, 60));
                }}
              >
                ⛔
              </button>
            )}
            {excerptMode && onExcerptRange && (
              <button
                className="rdx-seg-btn xpt-seg-btn"
                type="button"
                title="Include this line in the excerpt (first line sets the range, more lines extend it)"
                onClick={(e) => {
                  e.stopPropagation();
                  const end = seg.end ?? lines[i + 1]?.start ?? seg.start + 5;
                  onExcerptRange(seg.start ?? 0, end);
                }}
              >
                ✂
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
